/**
 * Opt-in gift with purchase offer widget (OL-3324 / OL-3326).
 *
 * Rendered by snippets/gwp-offer.liquid in the cart drawer and cart page.
 * Automatic campaigns are driven by Discount Kit's
 * `discount_kit:gift_adjustments` browser event; code campaigns are
 * server-rendered from Discount Kit Live data and take the same states.
 */
(() => {
  if (customElements.get('gwp-offer')) return;

  // Section Rendering swaps replace the element, so the last event payload is
  // cached at module level and re-applied when a new instance connects.
  const sharedState = (window.GWPOffer = window.GWPOffer || {
    lastAdjustments: null,
    giftVariantIds: null,
  });
  sharedState.giftVariantIds = sharedState.giftVariantIds || new Set();

  // ---- Elevar analytics (OL-3329) ----
  // Impressions push to window.ElevarDataLayer with list "gwp-offer" (nothing
  // auto-captures a widget render). Adds and removals are NOT pushed manually:
  // Elevar's Shopify source auto-captures cart adds and removals on-page
  // (confirmed on metagenics.com.au production), so manual pushes would
  // double-count. Instead the auto-captured events are decorated with GWP
  // attribution via a chained ElevarTransformFn, keyed on the gift's variant
  // id. Known limitation: if a customer holds a PAID line of the same variant
  // as their gift, removing the paid line also attributes to gwp-offer
  // (rare, analytics-only). Everything is guarded - analytics can never break
  // the cart flow.
  const GWP_LIST = 'gwp-offer';
  const seenImpressions = new Set();

  const markGiftVariant = (variantId) => {
    if (variantId !== null && typeof variantId !== 'undefined') {
      sharedState.giftVariantIds.add(String(variantId));
    }
  };

  const decorateGwpEvent = (data) => {
    if (!data || (data.event !== 'dl_add_to_cart' && data.event !== 'dl_remove_from_cart')) {
      return data;
    }
    const group =
      data.event === 'dl_add_to_cart'
        ? data.ecommerce && data.ecommerce.add
        : data.ecommerce && data.ecommerce.remove;
    if (!group || !Array.isArray(group.products)) return data;
    let isGiftEvent = false;
    group.products.forEach((product) => {
      if (sharedState.giftVariantIds.has(String(product.variant_id || ''))) {
        product.list = GWP_LIST;
        product.category = 'GWP';
        isGiftEvent = true;
      }
    });
    if (isGiftEvent) {
      group.actionField = group.actionField || {};
      group.actionField.list = GWP_LIST;
    }
    return data;
  };

  const previousTransformFn = window.ElevarTransformFn;
  window.ElevarTransformFn = (payload) => {
    let data = payload;
    if (typeof previousTransformFn === 'function') {
      try {
        data = previousTransformFn(data) || data;
      } catch (error) {
        console.error('gwp-offer: upstream ElevarTransformFn failed', error);
      }
    }
    try {
      data = decorateGwpEvent(data) || data;
    } catch (error) {
      console.error('gwp-offer: event decoration failed', error);
    }
    return data;
  };

  const centsToPrice = (cents) =>
    typeof cents === 'number' ? (cents / 100).toFixed(2) : String(cents || '0.00');

  const analyticsConfig = () => {
    const offer = document.querySelector('gwp-offer');
    return (
      (offer && offer.analytics) || { currencyCode: 'AUD', userProperties: { visitor_type: 'guest' } }
    );
  };

  const pushElevarEvent = (payload) => {
    try {
      window.ElevarDataLayer = window.ElevarDataLayer || [];
      window.ElevarDataLayer.push(payload);
    } catch (error) {
      console.error('gwp-offer: analytics push failed', error);
    }
  };

  const giftItemPayload = (gift) => ({
    id: gift.sku || String(gift.variantId || ''),
    name: gift.productTitle || '',
    brand: gift.vendor || '',
    category: 'GWP',
    variant: gift.variantTitle || '',
    price: centsToPrice(gift.price),
    quantity: '1',
    list: GWP_LIST,
    product_id: String(gift.productId || ''),
    variant_id: String(gift.variantId || ''),
    compare_at_price: centsToPrice(gift.compareAtPrice != null ? gift.compareAtPrice : gift.price),
    image: gift.image || '',
  });

  const trackGiftImpression = (gift) => {
    if (!gift) return;
    const key = `${gift.discountTitle || ''}|${gift.variantId || ''}`;
    if (seenImpressions.has(key)) return;
    seenImpressions.add(key);
    const config = analyticsConfig();
    pushElevarEvent({
      event: 'dl_view_item_list',
      user_properties: config.userProperties,
      ecommerce: {
        currencyCode: config.currencyCode,
        impressions: [{ ...giftItemPayload(gift), position: 1 }],
      },
    });
  };

  // Section targets across BOTH cart contexts. Removal must re-render
  // everything: it can fire from either context, and a context left stale
  // keeps dead line indices in its quantity inputs, wedging subsequent
  // /cart/change calls with "line parameter is invalid" (422).
  const allCartSectionTargets = () => {
    const components = [
      document.querySelector('cart-drawer-items'),
      document.querySelector('cart-items'),
    ].filter(Boolean);
    const targets = components.flatMap((component) =>
      typeof component.getSectionsToRender === 'function' ? component.getSectionsToRender() : []
    );
    const seen = new Set();
    return targets.filter((target) => {
      const key = `${target.id}|${target.section}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  const renderCartSections = (cart, targets) => {
    const sections = cart.sections || {};
    targets.forEach((target) => {
      const container = document.getElementById(target.id);
      const html = sections[target.section];
      if (!container || !html) return;
      const elementToReplace = (target.selector && container.querySelector(target.selector)) || container;
      const source = new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector(target.selector || '.shopify-section');
      if (source) elementToReplace.innerHTML = source.innerHTML;
    });
    if (typeof refreshAllBars === 'function') refreshAllBars(cart.total_price);
    if (typeof updateCartCounters === 'function') updateCartCounters(cart.item_count);
    if (typeof fireCartUpdated === 'function') fireCartUpdated(cart);
  };

  // Auto-remove (OL-3328): a gift line the discount no longer zeroes gets
  // removed. Never removes a $0 line - the discount function is the source
  // of truth, and Discount Kit's event references still-eligible gifts too
  // (observed in dev testing).
  let removalInFlight = false;
  const executeGiftRemoval = async (updates) => {
    if (!Object.keys(updates).length || removalInFlight) return;
    removalInFlight = true;
    try {
      const targets = allCartSectionTargets();
      const body = {
        updates,
        sections: [...new Set(targets.map((target) => target.section))],
        sections_url: window.location.pathname,
      };
      const updateResponse = await fetch(`${routes.cart_update_url}.js`, {
        ...fetchConfig(),
        body: JSON.stringify(body),
      });
      if (!updateResponse.ok) return;
      const updated = await updateResponse.json();
      document.querySelectorAll('gwp-offer').forEach((el) => {
        el.giftInCart = false;
      });
      renderCartSections(updated, targets);
    } catch (error) {
      console.error('gwp-offer: gift removal failed', error);
    } finally {
      removalInFlight = false;
    }
  };

  // Event-driven backstop: the fast path is the widget's own state check on
  // every re-render (see connectedCallback), which needs no cart fetch and no
  // wait on Discount Kit's script.
  const applyGiftRemovals = async (removeMap) => {
    const keys = Object.keys(removeMap || {});
    if (!keys.length || removalInFlight) return;
    try {
      const cartResponse = await fetch(`${routes.cart_url}.js`);
      const cart = await cartResponse.json();
      const updates = {};
      keys.forEach((key) => {
        const line = (cart.items || []).find((item) => item.key === key);
        if (line && line.properties && line.properties._dk_gift && line.final_line_price > 0) {
          updates[key] = 0;
        }
      });
      await executeGiftRemoval(updates);
    } catch (error) {
      console.error('gwp-offer: auto-remove failed', error);
    }
  };

  document.addEventListener('discount_kit:gift_adjustments', (event) => {
    sharedState.lastAdjustments = event.detail || null;
    document.querySelectorAll('gwp-offer').forEach((offer) => {
      if (typeof offer.applyGiftAdjustments === 'function') {
        offer.applyGiftAdjustments(sharedState.lastAdjustments);
      }
    });
    if (sharedState.lastAdjustments) {
      applyGiftRemovals(sharedState.lastAdjustments.remove);
    }
  });

  // Discount Kit dispatches this instead of refreshing the page when its own
  // script changes the cart (app embed refresh disabled); the theme owns the
  // re-render or carts fall out of sync with discount calculations.
  document.addEventListener('discount_kit:cart_changed', () => {
    ['cart-drawer-items', 'cart-items'].forEach((tagName) => {
      const component = document.querySelector(tagName);
      if (component && typeof component.onCartUpdate === 'function') component.onCartUpdate();
    });
  });

  const formatMoney = (cents) => {
    const currency =
      (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'AUD';
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(cents / 100);
  };

  class GwpOffer extends HTMLElement {
    connectedCallback() {
      this.imageEl = this.querySelector('.gwp-offer__image');
      this.vendorEl = this.querySelector('.gwp-offer__vendor');
      this.titleEl = this.querySelector('.gwp-offer__title');
      this.rrpEl = this.querySelector('.gwp-offer__rrp');
      this.button = this.querySelector('.gwp-offer__button');
      this.errorEl = this.querySelector('.gwp-offer__error');
      this.liveRegion = this.querySelector('[data-gwp-live]');
      this.busy = false;

      let parsed = { giftInCart: false, campaign: null };
      try {
        parsed = JSON.parse(this.querySelector('[data-gwp-state]').textContent);
      } catch (error) {
        console.error('gwp-offer: invalid state JSON', error);
      }
      this.giftInCart = parsed.giftInCart === true;
      this.giftLine = parsed.giftLine || null;
      this.campaign = parsed.campaign || null;
      this.appliedCodes = Array.isArray(parsed.appliedCodes) ? parsed.appliedCodes : [];
      this.analytics = parsed.analytics || null;

      // Register the gift variant so Elevar's auto-captured add/remove events
      // for it get GWP attribution (see ElevarTransformFn above).
      if (this.giftLine && this.giftLine.variantId) {
        markGiftVariant(this.giftLine.variantId);
      }

      // Server-rendered offer (code campaigns) counts as an impression
      if (!this.hidden && this.campaign) {
        trackGiftImpression(this.campaign);
      }

      this.button.addEventListener('click', this.onAddGiftClick.bind(this));

      // Fast-path auto-remove: every cart change re-renders this widget, so a
      // gift line the discount has stopped zeroing is caught immediately here
      // instead of waiting for Discount Kit's event. The DOM rows are cleared
      // optimistically so the customer sees the gift go instantly; the update
      // call corrects server state and re-renders behind it.
      if (this.giftLine && this.giftLine.finalPrice > 0) {
        document.querySelectorAll('.gwp-gift-line, .gwp-gift-banner-row').forEach((row) => row.remove());
        executeGiftRemoval({ [this.giftLine.key]: 0 });
      }

      if (sharedState.lastAdjustments) {
        this.applyGiftAdjustments(sharedState.lastAdjustments);
      }
    }

    applyGiftAdjustments(detail) {
      if (!detail || this.giftInCart) return;

      // The live event ships `add` as an ARRAY of adjustment objects (the
      // documented contract shows a single object - observed on dev with an
      // automatic campaign). Accept both shapes; take the first adjustment,
      // since v1 is single-gift.
      const adjustment = Array.isArray(detail.add) ? detail.add[0] : detail.add;
      const option =
        adjustment && Array.isArray(adjustment.options) ? adjustment.options[0] : null;
      const variant =
        option && Array.isArray(option.variants)
          ? option.variants.find((candidate) => candidate.available)
          : null;

      if (!option || !variant) {
        // No eligible automatic offer; a server-rendered code offer may still stand.
        if (!this.campaign || this.campaign.source === 'automatic') {
          this.campaign = null;
          this.hidden = true;
        }
        return;
      }

      this.campaign = {
        source: 'automatic',
        code: null,
        discountId: adjustment.discountId,
        discountTitle: adjustment.discountTitle,
        variantId: variant.id,
        variantTitle: variant.title,
        productId: option.id,
        productTitle: option.title,
        price: typeof variant.price === 'undefined' ? null : variant.price,
        image: variant.image || option.image || null,
      };
      this.renderOffer();
    }

    renderOffer() {
      const campaign = this.campaign;
      if (!campaign) return;

      if (campaign.image) {
        this.imageEl.src = campaign.image;
        this.imageEl.alt = campaign.productTitle || '';
        this.imageEl.hidden = false;
      }
      if (campaign.productTitle) this.titleEl.textContent = campaign.productTitle;
      if (campaign.price !== null && typeof campaign.price !== 'undefined') {
        this.rrpEl.textContent =
          typeof campaign.price === 'number' ? formatMoney(campaign.price) : campaign.price;
      }
      this.setError(false);
      this.hidden = false;
      trackGiftImpression(campaign);
    }

    onAddGiftClick() {
      if (this.busy || !this.campaign) return;
      this.busy = true;
      this.setError(false);
      this.button.textContent = this.button.dataset.addingLabel;
      this.button.setAttribute('disabled', '');
      this.classList.add('gwp-offer--busy');

      this.performAdd()
        .catch((error) => {
          console.error('gwp-offer: add failed', error);
          this.setError(true);
        })
        .finally(() => {
          this.busy = false;
          this.button.textContent = this.button.dataset.defaultLabel;
          this.button.removeAttribute('disabled');
          this.classList.remove('gwp-offer--busy');
        });
    }

    /**
     * One-click add (OL-3327). Code campaigns apply the discount code via the
     * AJAX cart API first; then the gift variant is added with the `_dk_gift`
     * line item property (Discount Kit's removal tracker and our gift-line
     * identifier), the gift line is verified at $0, and the cart re-renders.
     * On failed verification the gift line rolls back; an applied code stays
     * applied - it is legitimate cart state.
     */
    async performAdd() {
      const campaign = this.campaign;

      // Mark BEFORE the add call: Elevar auto-captures the request itself, so
      // the decoration must already know this variant is the gift.
      markGiftVariant(campaign.variantId);

      const addResponse = await fetch(`${routes.cart_add_url}.js`, {
        ...fetchConfig(),
        body: JSON.stringify({
          id: campaign.variantId,
          quantity: 1,
          properties: { _dk_gift: campaign.discountTitle || campaign.code || 'gwp' },
        }),
      });
      if (!addResponse.ok) {
        throw new Error(`gwp-offer: gift add failed (${addResponse.status})`);
      }

      // A single update call does the rest: applies the discount code (code
      // campaigns; the gift line must exist first or a GWP function discount
      // reports itself inapplicable), returns the cart JSON for the $0
      // verification, and carries the re-rendered section HTML. The discount
      // param REPLACES applied codes, so any codes already on the cart are
      // re-sent with ours.
      const updateBody = {
        sections: this.sectionsToRequest(),
        sections_url: window.location.pathname,
      };
      if (campaign.source === 'code' && campaign.code) {
        const codes = (this.appliedCodes || []).filter(
          (code) => code.toLowerCase() !== campaign.code.toLowerCase()
        );
        codes.push(campaign.code);
        updateBody.discount = codes.join(',');
      }

      const updateResponse = await fetch(`${routes.cart_update_url}.js`, {
        ...fetchConfig(),
        body: JSON.stringify(updateBody),
      });
      if (!updateResponse.ok) {
        throw new Error(`gwp-offer: cart update failed (${updateResponse.status})`);
      }
      const cart = await updateResponse.json();
      const giftLine = (cart.items || []).find((item) => item.properties && item.properties._dk_gift);

      if (!giftLine || giftLine.final_line_price > 0) {
        if (giftLine) {
          await fetch(`${routes.cart_change_url}.js`, {
            ...fetchConfig(),
            body: JSON.stringify({ id: giftLine.key, quantity: 0 }),
          });
        }
        this.refreshCart();
        throw new Error('gwp-offer: gift line was not discounted to $0');
      }

      // Mark every widget instance, not just this one: the other context's
      // sections aren't re-rendered, and its stale widget must not re-offer.
      document.querySelectorAll('gwp-offer').forEach((offer) => {
        offer.giftInCart = true;
        offer.hidden = true;
      });
      this.liveRegion.textContent = this.button.dataset.addedMessage;
      this.renderSections(cart);
    }

    /* The cart components own the section-splice contract; reuse their maps
       so the update response re-renders without extra fetches (the same
       pattern quantity updates use). Only the clicked widget's context is
       rendered - matching the theme, which also leaves the closed drawer
       stale on cart-page quantity changes - so the cart page doesn't pay for
       a drawer render on every add. */
    sectionTargets() {
      const inDrawer = this.classList.contains('gwp-offer--drawer');
      const component =
        (inDrawer
          ? document.querySelector('cart-drawer-items')
          : document.querySelector('cart-items')) ||
        document.querySelector('cart-drawer-items') ||
        document.querySelector('cart-items');
      return component && typeof component.getSectionsToRender === 'function'
        ? component.getSectionsToRender()
        : [];
    }

    sectionsToRequest() {
      return [...new Set(this.sectionTargets().map((target) => target.section))];
    }

    renderSections(cart) {
      renderCartSections(cart, this.sectionTargets());
    }

    refreshCart(cartData) {
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'gwp-offer', cartData });
        return;
      }
      const cartItems = document.querySelector('cart-drawer-items') || document.querySelector('cart-items');
      if (cartItems && typeof cartItems.onCartUpdate === 'function') cartItems.onCartUpdate();
    }

    setError(visible) {
      this.errorEl.hidden = !visible;
      if (visible) this.liveRegion.textContent = this.errorEl.textContent;
    }
  }

  customElements.define('gwp-offer', GwpOffer);
})();
